/**
 * useSession Hook
 * Manages user optimization sessions with autosave and recovery
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import sessionApiService from '../services/sessionApi';

export const useSession = (modelId, hardware) => {
  const { session } = useAuth();
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionLayers, setSessionLayers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [hasRecoverableSessions, setHasRecoverableSessions] = useState(false);
  const [checkingForSessions, setCheckingForSessions] = useState(true);
  const [autosaving, setAutosaving] = useState(false);

  // Autosave queue and debounce
  const autosaveQueue = useRef(new Map());
  const autosaveTimer = useRef(null);

  /**
   * Check for existing active sessions on mount
   */
  useEffect(() => {
    if (!modelId || !session?.access_token) {
      setCheckingForSessions(false);
      return;
    }

    const checkForSessions = async () => {
      setCheckingForSessions(true);
      try {
        const result = await sessionApiService.findActiveSession(
          modelId,
          session.access_token
        );

        console.log('Session recovery check result:', result);

        if (result.has_sessions && result.sessions.length > 0) {
          console.log('Found sessions:', result.sessions);
          setHasRecoverableSessions(true);
          setAvailableSessions(result.sessions);
          // Don't auto-load a session - let user choose
        } else {
          console.log('No sessions found');
          setHasRecoverableSessions(false);
          setAvailableSessions([]);
        }
      } catch (err) {
        console.error('Error checking for sessions:', err);
        setHasRecoverableSessions(false);
        setAvailableSessions([]);
      } finally {
        setCheckingForSessions(false);
      }
    };

    checkForSessions();
  }, [modelId, session?.access_token]);

  /**
   * Create a new session
   */
  const createSession = useCallback(async (sessionName = null) => {
    if (!modelId || !session?.access_token) {
      throw new Error('Missing required parameters');
    }

    setLoading(true);
    setError(null);

    try {
      const newSession = await sessionApiService.createSession(
        modelId,
        hardware,
        sessionName,
        session.access_token
      );

      setCurrentSession(newSession);
      setSessionLayers({}); // Clear old session layers for fresh start
      setHasRecoverableSessions(false);
      setAvailableSessions([]);

      // Topology analysis removed - deprecated feature

      return newSession;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [modelId, hardware, session?.access_token]);

  /**
   * Load session layers
   */
  const loadSessionLayers = useCallback(async (sessionId) => {
    if (!session?.access_token) return;

    try {
      const layers = await sessionApiService.getSessionLayers(
        sessionId,
        false,
        session.access_token
      );

      // Convert array to map for easy access
      // Use session_layer.id as key (not layer_id, which is NULL for merged layers)
      const layersMap = {};
      layers.forEach(layer => {
        // For compatibility, also store by layer_id if it exists (non-merged layers)
        const key = layer.layer_id || layer.id;
        layersMap[key] = layer;
      });

      setSessionLayers(layersMap);
      return layersMap;
    } catch (err) {
      console.error('Error loading session layers:', err);
      throw err;
    }
  }, [session?.access_token]);


  /**
   * Flush autosave queue immediately (no debounce)
   */
  const flushAutosave = useCallback(async () => {
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }

    if (autosaveQueue.current.size === 0) {
      return;
    }

    setAutosaving(true);
    const updates = Array.from(autosaveQueue.current.entries());
    autosaveQueue.current.clear();

    // Process all queued autosaves
    for (const [, data] of updates) {
      try {
        // Autosave works for both regular and merged layers now
        // Backend will query by layer_id OR id depending on which exists
        await sessionApiService.autosaveLayer(
          currentSession.id,
          data.layerId,
          data.code,
          data.technique,
          data.notes,
          session.access_token
        );

        // Update local state
        setSessionLayers(prev => ({
          ...prev,
          [data.layerId]: {
            ...prev[data.layerId],
            current_code: data.code,
            is_modified: true,
            updated_at: new Date().toISOString()
          }
        }));

        console.log(`Saved layer ${data.layerId || 'merged'}`);
      } catch (err) {
        console.error(`Failed to save layer ${data.layerId || 'merged'}:`, err);
        throw err;
      }
    }

    setAutosaving(false);
  }, [currentSession, session?.access_token, sessionLayers]);

  /**
   * Autosave layer code (debounced)
   * Only triggers after user stops typing for 1 second
   */
  const autosaveLayer = useCallback((layerId, code, technique = null, notes = null) => {
    if (!currentSession) return;

    // Handle NULL layer_id (merged layers) - use session layer id as key
    const autosaveKey = layerId || 'merged_layer_' + Date.now();

    // Add to queue
    autosaveQueue.current.set(autosaveKey, { layerId, code, technique, notes });

    // Clear existing timer (debounce - restart timer on each keystroke)
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
    }

    // Set new timer (autosave after 1 second of inactivity)
    autosaveTimer.current = setTimeout(async () => {
      await flushAutosave();
    }, 1000); // 1 second debounce
  }, [currentSession, flushAutosave]);

  /**
   * Save execution result
   */
  const saveExecutionResult = useCallback(async (layerId, status, output = null, metrics = null) => {
    if (!currentSession || !session?.access_token) return;

    try {
      await sessionApiService.saveExecutionResult(
        currentSession.id,
        layerId,
        status,
        output,
        metrics,
        session.access_token
      );

      // Update local state
      setSessionLayers(prev => ({
        ...prev,
        [layerId]: {
          ...prev[layerId],
          last_execution_status: status,
          last_execution_output: output,
          last_execution_metrics: metrics,
          last_execution_at: new Date().toISOString()
        }
      }));

      return true;
    } catch (err) {
      console.error('Failed to save execution result:', err);
      return false;
    }
  }, [currentSession, session?.access_token]);

  /**
   * Update session metadata
   */
  const updateSession = useCallback(async (updates) => {
    if (!currentSession || !session?.access_token) return;

    try {
      const updated = await sessionApiService.updateSession(
        currentSession.id,
        updates,
        session.access_token
      );

      setCurrentSession(updated);
      return updated;
    } catch (err) {
      console.error('Failed to update session:', err);
      throw err;
    }
  }, [currentSession, session?.access_token]);

  /**
   * Complete session
   */
  const completeSession = useCallback(async () => {
    return await updateSession({ status: 'completed' });
  }, [updateSession]);

  /**
   * Delete session
   */
  const deleteSession = useCallback(async (hardDelete = false) => {
    if (!currentSession || !session?.access_token) return;

    try {
      await sessionApiService.deleteSession(
        currentSession.id,
        hardDelete,
        session.access_token
      );

      setCurrentSession(null);
      setSessionLayers({});
      setHasRecoverableSessions(false);
      setAvailableSessions([]);
    } catch (err) {
      console.error('Failed to delete session:', err);
      throw err;
    }
  }, [currentSession, session?.access_token]);

  /**
   * Export session
   */
  const exportSession = useCallback(async () => {
    if (!currentSession || !session?.access_token) return null;

    try {
      const exportData = await sessionApiService.exportSession(
        currentSession.id,
        session.access_token
      );

      // Trigger download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session-${currentSession.id}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);

      return exportData;
    } catch (err) {
      console.error('Failed to export session:', err);
      throw err;
    }
  }, [currentSession, session?.access_token]);

  /**
   * Get layer code from session
   * Session layers are always cloned from model_layers on session creation,
   * so this should always return valid code (current_code or baseline_code)
   */
  const getLayerCode = useCallback((layerId) => {
    const sessionLayer = sessionLayers[layerId];
    console.log('getLayerCode called for layer:', layerId);
    console.log('Session layer data:', sessionLayer);

    if (!sessionLayer) {
      console.warn('Session layer not found - session may be corrupted or not loaded yet');
      return null;
    }

    // Return current_code if available (user has edited), otherwise baseline_code
    const code = sessionLayer.current_code || sessionLayer.baseline_code;
    console.log('Returning code:', code ? `${code.substring(0, 50)}...` : '(empty code)');

    return code || null;
  }, [sessionLayers]);

  /**
   * Check if layer is modified
   */
  const isLayerModified = useCallback((layerId) => {
    return sessionLayers[layerId]?.is_modified || false;
  }, [sessionLayers]);

  /**
   * Get I/O metadata for a layer (both golden and session)
   */
  const getLayerIOMetadata = useCallback(async (layerId) => {
    if (!currentSession || !session?.access_token) {
      console.warn('Cannot get I/O metadata: no session or access token');
      return null;
    }

    try {
      const metadata = await sessionApiService.getLayerIOMetadata(
        currentSession.id,
        layerId,
        session.access_token
      );
      return metadata;
    } catch (err) {
      console.error('Failed to get I/O metadata:', err);
      return null;
    }
  }, [currentSession, session?.access_token]);

  /**
   * Update session-level I/O metadata (experimental/mutable)
   */
  const updateSessionIOMetadata = useCallback(async (layerId, inputMetadata, outputMetadata) => {
    if (!currentSession || !session?.access_token) {
      console.warn('Cannot update I/O metadata: no session or access token');
      return false;
    }

    try {
      await sessionApiService.updateSessionIOMetadata(
        currentSession.id,
        layerId,
        inputMetadata,
        outputMetadata,
        session.access_token
      );
      return true;
    } catch (err) {
      console.error('Failed to update session I/O metadata:', err);
      return false;
    }
  }, [currentSession, session?.access_token]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
      }
    };
  }, []);

  /**
   * Load a specific session by ID
   */
  const loadSession = useCallback(async (sessionId) => {
    if (!session?.access_token) return;

    try {
      console.log('Loading session:', sessionId);
      const sessionData = await sessionApiService.getSession(sessionId, session.access_token);
      console.log('Session data loaded:', sessionData);

      setCurrentSession(sessionData);

      const layers = await loadSessionLayers(sessionData.id);
      console.log('Session layers loaded:', layers);

      // Topology analysis removed - deprecated feature

      setHasRecoverableSessions(false);
      setAvailableSessions([]);
      return sessionData;
    } catch (err) {
      console.error('Failed to load session:', err);
      throw err;
    }
  }, [session?.access_token, loadSessionLayers, modelId]);

  return {
    // State
    currentSession,
    sessionLayers,
    loading,
    error,
    hasRecoverableSessions,
    availableSessions,
    checkingForSessions,
    autosaving,

    // Methods
    createSession,
    loadSession,
    loadSessionLayers,
    autosaveLayer,
    flushAutosave,
    saveExecutionResult,
    updateSession,
    completeSession,
    deleteSession,
    exportSession,
    getLayerCode,
    isLayerModified,
    getLayerIOMetadata,
    updateSessionIOMetadata
  };
};
